import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DatePicker,
  Select,
  Spin,
  Checkbox,
  Form,
  Row,
  Col,
  Button,
  notification,
  Modal,
} from "antd";
import styled from "styled-components";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import Table from "atoms/Table";
import queryKeys from "common/queryKey";
import moment from "moment";
import { useScrollFetchPost2 } from "hooks/useScrollFetchPost2";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import { useQueryClient } from "react-query";
import { useUpdate } from "hooks/useUpdate";
import {
  CheckCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { CALL_API } from "common/API";

const { RangePicker } = DatePicker;

const formatAmount = (value) => {
  const num = Number(value?.toString().replace(/[^0-9.-]+/g, ""));
  return isNaN(num)
    ? value
    : num.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

const generateWeeksBetweenDates = (dateRange) => {
  if (!dateRange?.length) return [];
  let startWeek = moment(dateRange[0]).startOf("isoWeek");
  const endWeek = moment(dateRange[1]).endOf("isoWeek");
  const weeks = [];

  while (startWeek <= endWeek) {
    let endOfWeek = moment(startWeek).add(6, "days");
    weeks.push(
      `${startWeek.format("YYYYMMMDD").toUpperCase()}-${endOfWeek
        .format("YYYYMMMDD")
        .toUpperCase()}`
    );
    startWeek = startWeek.add(1, "week");
  }
  return weeks;
};

const options = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Declined", value: "declined" },
];

const TimeSheetApproval = ({ timeSheetPermission }) => {
  const [form] = Form.useForm();
  const history = useHistory();
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "months").startOf("month"),
    moment().endOf("month"),
  ]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [status, setStatus] = useState(["pending"]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [typeOptions, setTypeOptions] = useState([
    { label: "C", value: "C" },
    { label: "NC", value: "NC" },
    { label: "AP", value: "AP" },
  ]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalBulkApproveOpen, setIsModalBulkApproveOpen] = useState(false);
  const queryClient = useQueryClient();
  const weeksPayload =
  status?.[0]?.toLowerCase() === "pending"
    ? []
    : generateWeeksBetweenDates(dateRange);

  const { getAdminApprovalTimesheet } = queryKeys;
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);
  const country = currentUser?.managingoffice?.[0];

  const { key, url } = getAdminApprovalTimesheet(JSON.stringify(weeksPayload));
  const {
    data,
    isLoading: loader,
    refetch,
  } = useScrollFetchPost2(`${key}-${status}`, {
    endPoint: url,
    queryParam: "LastEvaluatedKey",
    extra: {
      weeks: weeksPayload,
      statusArray: [...status],
      employeeId: selectedEmployees,
      userId: userInfo?.userid,
      country: country,
    },
  });

  const invalidateTimeSheet = useCallback(async () => {
    setLoading(true);
    try {
      await refetch();
    } finally {
      setLoading(false);
    }
  }, [refetch]);

  const timesheetsData = useMemo(() => {
    if (!data) return [];
    return data?.pages?.reduce((acc, page) => {
      if (!page?.response?.response) return acc;
      return [...acc, ...page?.response?.response];
    }, []);
  }, [data]);
  const [timeSheets, setTimeSheets] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [approvalStatuses, setApprovalStatuses] = useState({});

  useEffect(() => {
    const selectedData = timeSheets.filter((row) =>
      selectedRowKeys.includes(row.key)
    );
    setSelectedRowData(selectedData); // You'll use this in the modal
  }, [selectedRowKeys, timeSheets]);

  useEffect(() => {
    if (timesheetsData) {
      const uniqueEmployeesSet = new Set();
      const uniqueEmployees = [];
      timesheetsData.forEach((t) => {
        if (!uniqueEmployeesSet.has(t.employeeId)) {
          uniqueEmployeesSet.add(t.employeeId);
          uniqueEmployees.push({
            label: t.employeeName,
            value: t.employeeId,
          });
        }
      });
      setEmployeeOptions(uniqueEmployees);
      const filteredData = timesheetsData
        ?.filter((timesheet) =>
          selectedEmployees?.length > 0
            ? selectedEmployees.includes(timesheet.employeeId)
            : true
        )
        ?.filter((timesheet) => {
          const type = timesheet?.timeSheet?.some(
            (sec) => sec.engagement === "nc"
          )
            ? "NC"
            : "C";
          return selectedTypes?.length > 0
            ? selectedTypes.includes(type)
            : true;
        })
        ?.map((entry) => ({
          ...entry,
          timeSheet: entry.timeSheet.filter(
            (sheet) => true // Include all entries without filtering by status
          ),
        }))
        .filter((item) => {
          const sheet = item?.timeSheet;
          return sheet?.some(
            (entry) =>
              entry.personInCharge === userInfo?.userid &&
              status.includes(entry.status)
          );
        })
        ?.filter((entry) => entry.timeSheet?.length > 0)
        // ?.filter((timesheet) =>
        //   status.length > 0 ? status.includes(timesheet.status) : true
        // )
        ?.map((timesheet, index) => {
          const workingHours = timesheet?.timeSheet?.reduce((acc, day) => {
            return (
              acc +
              (day?.mon || 0) +
              (day?.tue || 0) +
              (day?.wed || 0) +
              (day?.thu || 0) +
              (day?.fri || 0) +
              (day?.sat || 0) +
              (day?.sun || 0)
            );
          }, 0);
          const totalClaims = timesheet?.timeSheet?.reduce((acc, day) => {
            return (
              acc +
              day?.claims?.reduce(
                (acc, claim) => acc + (parseInt(claim?.amount) || 0),
                0
              )
            );
          }, 0);
          const totalTasks = timesheet?.timeSheet?.reduce((acc, day) => {
            return acc + (day?.task ? 1 : 0);
          }, 0);
          const formattedPeriod = `${moment(
            timesheet?.week.split("-")[0],
            "YYYYMMMDD"
          ).format("DD-MMM-YYYY")} to ${moment(
            timesheet?.week.split("-")[1],
            "YYYYMMMDD"
          ).format("DD-MMM-YYYY")}`;
          const hasNC = timesheet?.timeSheet?.some(
            (sec) => sec.engagement === "nc"
          );
          const hasC = timesheet?.timeSheet?.some(
            (sec) => sec.engagement !== "nc"
          );
          const type = hasNC && hasC ? "NC/C" : hasNC ? "NC" : "C";
          return {
            key: timesheet?.timeSheetId, // Ensure each item has a unique key
            timesheet_id: timesheet?.timeSheetId,
            week: timesheet?.week,
            type: type,
            employeeId: timesheet?.employeeId,
            employee_name: (
              <div className="employee-name">{timesheet?.employeeName}</div>
            ),
            period: <div className="period">{formattedPeriod}</div>,
            timesheet_hours: workingHours,
            total_claims: formatAmount(totalClaims),
            total_tasks: totalTasks,
          };
        });
      setTimeSheets(filteredData);
      // Ensure selectedRowKeys are within the new data set
      setSelectedRowKeys((prevKeys) =>
        prevKeys.filter((key) => filteredData.some((item) => item.key === key))
      );
    }
  }, [timesheetsData, selectedEmployees, selectedTypes, status, dateRange]);

  const { updateTimesheet, getTimesheet } = queryKeys;
  const { week } = useParams();

  const handleBulkApprovalModal = () => {
    setIsModalBulkApproveOpen(true);
  };

  const handleSelectAllCheckBoxchange = (e) => {
    const isChecked = e.target.checked;

    const pendingKeys = timeSheets
      .filter((item) => {
        const sheet = timesheetsData.find(
          (t) => t.timeSheetId === item.timesheet_id
        )?.timeSheet;

        return sheet?.some(
          (entry) =>
            entry.personInCharge === userInfo?.userid &&
            entry.status === "pending"
        );
      })
      .map((item) => item.key); // assuming `key` is unique and set in each row

    if (isChecked) {
      setSelectedRowKeys(pendingKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleSingleCheckBoxchange = (e, record) => {
    const checked = e.target.checked;
    setSelectedRowKeys((prevSelected) => {
      if (checked) {
        return [...prevSelected, record.key];
      } else {
        return prevSelected.filter((key) => key !== record.key);
      }
    });
  };

  const getActualToDate = async (engagement, resourceId) => {
    try {
      const { data } = await CALL_API(
        `/insyts/timesheet?engagementId=${engagement}&resourceId=${resourceId}`,
        "get"
      );

      return data?.[0];
    } catch (error) {
      console.error("Failed to fetch actualToDate:", error);
      return null;
    }
  };

  const getActualSummary = async (engagementId, userId) => {
    try {
      const { data } = await CALL_API(
        `/insyts/timesheet/actualsummary?engagementId=${engagementId}&userId=${userId}`,
        "get"
      );

      return data;
    } catch (error) {
      console.error("Failed to fetch actualToDate:", error);
      return null;
    }
  };

  const approveTimesheet = async () => {
    if (!selectedRowKeys?.length) {
      notification.warning({ message: "No timesheets selected" });
      return;
    }
    setLoading(true);
    for (const id of selectedRowKeys) {
      const timesheetIndex = timesheetsData.findIndex(
        (item) => item.timeSheetId === id
      );
      if (timesheetIndex === -1) continue;
      const oldData = timesheetsData[timesheetIndex];
      const updatedTimeSheet = await Promise.all(
        (oldData?.timeSheet || []).map(async (item) => {
          const actualToDate = await getActualToDate(
            item?.engagement,
            item?.resourceId
          );
          if (item.personInCharge === userInfo?.userid) {
            return {
              ...item,
              actual_to_date: actualToDate?.actualmandays || 0,
              status: "approved",
              approval: {
                by: userInfo.employer_id,
                date: moment().format("DD-MM-YYYY"),
              },
            };
          }
          return item;
        })
      );
      const payload = {
        ...oldData,
        updatedTimeSheet:
          updatedTimeSheet
            ?.filter((item) => item.personInCharge === userInfo?.userid)
            ?.map((item) => ({ ...item, status: "approved" })) || [],
        timeSheet: updatedTimeSheet,
        status: updatedTimeSheet?.some((entry) => entry.status === "pending")
          ? "pending"
          : "approved",
      };

      setApprovalStatuses((prev) => ({ ...prev, [id]: "pending" }));
      await new Promise((resolve) => {
        approveUpdate(payload, {
          onSuccess: async () => {
            setApprovalStatuses((prev) => ({ ...prev, [id]: "approved" }));
            const updatedTimeSheet = await Promise.all(
              (oldData?.timeSheet || []).map(async (item) => {
                const actualSummary = await getActualSummary(
                  item?.engagement,
                  oldData?.employeeId
                );

                if (item.personInCharge === userInfo?.userid) {
                  const hasExisting =
                    (actualSummary?.actualTimesheet || []).length > 0;

                  const actualTimesheet = hasExisting
                    ? actualSummary.actualTimesheet.map((summaryItem) => ({
                        ...summaryItem,
                        status:
                          summaryItem?.week === oldData?.week
                            ? "approved"
                            : summaryItem?.status,
                      }))
                    : [
                        {
                          resourceId: item.resourceId ?? null,
                          week: oldData?.week,
                          sum: item.sum || 0,
                          actualhours: item.actual_to_date ?? 0,
                          status: "approved",
                        },
                      ];

                  return {
                    engagementId: item?.engagement,
                    userId: oldData?.employeeId,
                    actualTimesheet,
                    _method: hasExisting ? "patch" : "post",
                  };
                }

                return item;
              })
            );
            for (const entry of updatedTimeSheet) {
              if (entry?.actualTimesheet) {
                const { _method, ...actualSummaryPayload } = entry;
                const { code } = await CALL_API(
                  `insyts/timesheet/actualsummary`,
                  _method || "patch",
                  actualSummaryPayload
                );
              }
            }
            resolve(); // just resolve
          },
          onError: () => {
            setApprovalStatuses((prev) => ({ ...prev, [id]: "declined" }));
            resolve(); // still resolve even if failed
          },
        });
      });
    }
    // ✅ Only run after all approvals done
    await queryClient.invalidateQueries([getTimesheet.key, `week:${week}`]);
    await invalidateTimeSheet();
    notification.success({ message: "Bulk approval completed" });
    setIsModalBulkApproveOpen(false); // ✅ Close modal only after all done
    setLoading(false);
  };

  // const approveTimesheet = () => {
  //   setLoading(true);
  //   if (!selectedRowKeys?.length) {
  //     notification.warning({ message: "No timesheets selected" });
  //     setLoading(false);
  //     return;
  //   }
  //   selectedRowKeys.forEach((id) => {
  //     const timesheetIndex = timesheetsData.findIndex(item => item.timeSheetId === id);
  //     if (timesheetIndex === -1) return;
  //     const oldData = timesheetsData[timesheetIndex];
  //     const updatedTimeSheet = oldData.timeSheet?.map((entry) => ({
  //       ...entry,
  //       status: "approved",
  //       approval: {
  //         by: userInfo.employer_id,
  //         date: moment().format("DD-MM-YYYY"),
  //       },
  //     })) || [];

  //     const payload = {
  //       ...oldData,
  //       updatedTimeSheet,
  //       timeSheet: updatedTimeSheet,
  //       status: "approved",
  //     };
  //     console.log(payload, "payload");

  //     // approveUpdate(payload, {
  //     //   onSuccess: () => {
  //     //     // queryClient.invalidateQueries(`${getTimesheet.key}-week:${week}`);
  //     //     queryClient.invalidateQueries([getTimesheet.key, `week:${week}`]);
  //     //     setLoading(false);
  //     //     refetch()
  //     //     notification.success({
  //     //       message: "Timesheets Approved Successfully",
  //     //     });
  //     //     setSelectedRowKeys([]);
  //     //   },
  //     //   onError: (err) => {
  //     //     console.error("Approval failed", err);
  //     //     notification.error({
  //     //       message: "Failed to approve timesheet",
  //     //     });
  //     //     setLoading(false);
  //     //   },
  //     // });
  //   });
  //   // setSelectedRowKeys([]);
  // };

  const { mutate: approveUpdate } = useUpdate(
    updateTimesheet.key,
    updateTimesheet.url
  );

  const initialTimeSheetColumns = [
    {
      title:
        status.includes("pending") &&
        timeSheets.filter((item) => {
          const sheet = timesheetsData.find(
            (t) => t.timeSheetId === item.timesheet_id
          )?.timeSheet;
          return sheet?.some(
            (entry) =>
              entry.personInCharge === userInfo?.userid &&
              entry.status === "pending"
          );
        })?.length ? (
          <Checkbox
            onClick={(e) => e.stopPropagation()}
            checked={
              selectedRowKeys?.length === timeSheets?.length &&
              timeSheets?.length > 0
            }
            indeterminate={
              selectedRowKeys?.length > 0 &&
              selectedRowKeys?.length < timeSheets?.length
            }
            onChange={handleSelectAllCheckBoxchange}
          />
        ) : null,
      dataIndex: "checkbox",
      render: (_, record, index) => {
        const statusData = timesheetsData?.find(
          (item) => item?.timeSheetId === record?.timesheet_id
        )?.status;
        if (!statusData?.includes("pending") || !status.includes("pending")) {
          return null;
        } else {
          return (
            <Checkbox
              onClick={(e) => e.stopPropagation()}
              checked={selectedRowKeys?.includes(record.key)}
              onChange={(e) => handleSingleCheckBoxchange(e, record)}
            />
          );
        }
      },
      width: 50,
    },
    {
      title: "Period",
      dataIndex: "period",
      key: "period",
      width: 230,
    },
    {
      title: "Name",
      dataIndex: "employee_name",
      key: "employee_name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Timesheet Hours",
      dataIndex: "timesheet_hours",
      key: "timesheet_hours",
      align: "center",
    },
    {
      title: "Total Claims",
      dataIndex: "total_claims",
      key: "total_claims",
      align: "center",
    },
    {
      title: "Total Tasks",
      dataIndex: "total_tasks",
      key: "total_tasks",
      align: "center",
    },
  ];

  const [timeSheetColumns, setTimeSheetColumns] = useState(
    initialTimeSheetColumns
  );

  useEffect(() => {
    const newColumns = [
      {
        title:
          status.includes("pending") &&
          timeSheets.filter((item) => {
            const sheet = timesheetsData.find(
              (t) => t.timeSheetId === item.timesheet_id
            )?.timeSheet;
            return sheet?.some(
              (entry) =>
                entry.personInCharge === userInfo?.userid &&
                entry.status === "pending"
            );
          })?.length ? (
            <Checkbox
              onClick={(e) => e.stopPropagation()}
              checked={
                timeSheets.filter((item) => {
                  const sheet = timesheetsData.find(
                    (t) => t.timeSheetId === item.timesheet_id
                  )?.timeSheet;
                  return sheet?.some(
                    (entry) =>
                      entry.personInCharge === userInfo?.userid &&
                      entry.status === "pending"
                  );
                })?.length > 0 &&
                selectedRowKeys?.length ===
                  timeSheets.filter((item) => {
                    const sheet = timesheetsData.find(
                      (t) => t.timeSheetId === item.timesheet_id
                    )?.timeSheet;
                    return sheet?.some(
                      (entry) =>
                        entry.personInCharge === userInfo?.userid &&
                        entry.status === "pending"
                    );
                  })?.length
              }
              indeterminate={
                selectedRowKeys?.length > 0 &&
                selectedRowKeys?.length <
                  timeSheets.filter((item) => {
                    const sheet = timesheetsData.find(
                      (t) => t.timeSheetId === item.timesheet_id
                    )?.timeSheet;
                    return sheet?.some(
                      (entry) =>
                        entry.personInCharge === userInfo?.userid &&
                        entry.status === "pending"
                    );
                  })?.length
              }
              onChange={handleSelectAllCheckBoxchange}
            />
          ) : null,
        dataIndex: "checkbox",
        key: "checkbox",
        width: 50,
        render: (_, record) => {
          const sheet = timesheetsData.find(
            (item) => item.timeSheetId === record.timesheet_id
          )?.timeSheet;

          const isPending = sheet?.some(
            (entry) =>
              entry.personInCharge === userInfo?.userid &&
              entry.status === "pending"
          );

          if (!isPending || !status.includes("pending")) {
            return null;
          }

          return (
            <Checkbox
              onClick={(e) => e.stopPropagation()}
              checked={selectedRowKeys.includes(record.key)}
              onChange={(e) => handleSingleCheckBoxchange(e, record)}
            />
          );
        },
      },

      {
        title: "Period",
        dataIndex: "period",
        key: "period",
        width: 230,
      },
      {
        title: "Name",
        dataIndex: "employee_name",
        key: "employee_name",
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
      },
      {
        title: "Timesheet Hours",
        dataIndex: "timesheet_hours",
        key: "timesheet_hours",
        align: "center",
      },
      {
        title: "Total Claims",
        dataIndex: "total_claims",
        key: "total_claims",
        align: "center",
      },
      {
        title: "Total Tasks",
        dataIndex: "total_tasks",
        key: "total_tasks",
        align: "center",
      },
    ];

    setTimeSheetColumns(newColumns);
  }, [timeSheets, selectedRowKeys, status, timesheetsData, userInfo]);

  if (loader) {
    return (
      <div style={{ textAlign: "center", marginTop: "20%" }}>
        <Spin size="large" tip="Loading timesheets..." />
      </div>
    );
  }

  return (
    <>
      {isModalBulkApproveOpen && (
        <>
          <Modal
            title="Approve Selected Timesheets"
            visible={isModalBulkApproveOpen}
            // onCancel={() => setIsModalBulkApproveOpen(false)}
            footer={null}
            onCancel={() => {
              if (!loading) setIsModalBulkApproveOpen(false); // block cancel while loading
            }}
            closable={!loading}
            maskClosable={!loading}
          >
            <div style={{ overflowY: "scroll", maxHeight: "300px" }}>
              {selectedRowData?.map((row) => {
                const timesheetId = row?.timeSheetId ?? row?.key;
                const status = approvalStatuses[timesheetId];
                return (
                  <div
                    key={timesheetId}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      marginBottom: "8px",
                      backgroundColor: "#F9F9F9",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ flex: 2 }}>
                      <strong style={{ color: "#397EF3" }}>
                        {row?.employee_name?.props?.children ??
                          row?.employee_name}
                      </strong>
                    </div>
                    <div style={{ flex: 2, marginLeft: 20 }}>
                      {row?.period?.props?.children ?? row?.period}
                    </div>
                    <div style={{ flex: 2, marginLeft: 20 }}>{row?.type}</div>
                    <div style={{ flex: 2 }}>{row?.timesheet_hours}</div>
                    <div style={{ marginLeft: 8 }}>
                      {status === "pending" && (
                        <LoadingOutlined style={{ color: "#1890FF" }} />
                      )}
                      {status === "approved" && (
                        <CheckCircleOutlined style={{ color: "green" }} />
                      )}
                      {status === "declined" && (
                        <CloseCircleOutlined style={{ color: "red" }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="d-flex justify-content-end align-items-center gap-2 mt-2">
              <Button
                key="cancel"
                onClick={() => setIsModalBulkApproveOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              ,
              <Button
                type="primary"
                onClick={() => {
                  approveTimesheet();
                }}
                loading={loading}
              >
                Approve All
              </Button>
            </div>
          </Modal>
        </>
      )}
      <TimeSheetWrap>
        <div
          className="filters"
          style={{ minHeight: "80px", overflow: "hidden" }}
        >
          <Form
            form={form}
            onValuesChange={(changedValues, allValues) => {
              if (changedValues.employees)
                setSelectedEmployees(changedValues.employees);
              if (changedValues.selectedTypes)
                setSelectedTypes(changedValues.selectedTypes);
              if (changedValues.status_id) setStatus(changedValues.status_id);
            }}
          >
            <Row gutter={16} align="middle" wrap>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="employees"
                  rules={[
                    { required: true, message: "Please select employees" },
                  ]}
                  style={{ marginBottom: "8px", width: "300px" }}
                >
                  {/* <DropdownCheckBox
                    name="employees"
                    label="Select Employees"
                    items={employeeOptions}
                    options={employeeOptions?.map((employee) => ({
                      label:
                        typeof employee.label?.props?.children === "string"
                          ? employee.label.props.children
                          : employee.label || "",
                      value: employee.value,
                    }))}
                    form={form}
                    selectedValues={selectedEmployees}
                    onChange={setSelectedEmployees}
                    dropdownMatchSelectWidth={false}
                  /> */}
                  <Select
                    placeholder="Select Employee"
                    style={{ flex: 1, width: "100%" }}
                    onChange={(value) => setSelectedEmployees([value])}
                    value={selectedEmployees[0] || undefined}
                    options={employeeOptions?.map((employee) => ({
                      label:
                        typeof employee.label?.props?.children === "string"
                          ? employee.label.props.children
                          : employee.label || "",
                      value: employee.value,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="selectedTypes"
                  rules={[{ required: true, message: "Please select types" }]}
                  style={{ marginBottom: "8px", width: "300px" }}
                >
                  {/* <DropdownCheckBox
                    name="selectedTypes"
                    label="Select Types"
                    items={typeOptions}
                    options={typeOptions?.map((type) => ({
                      label:
                        typeof type.label?.props?.children === "string"
                          ? type.label.props.children
                          : type.label || "",
                      value: type.value,
                    }))}
                    form={form}
                    selectedValues={selectedTypes}
                    onChange={setSelectedTypes}
                    dropdownMatchSelectWidth={false}
                  /> */}
                  <Select
                    placeholder="Select Types"
                    style={{ flex: 1, width: "100%" }}
                    onChange={(value) => setSelectedTypes([value])}
                    value={selectedTypes[0] || undefined}
                    options={typeOptions?.map((type) => ({
                      label:
                        typeof type.label === "object" &&
                        type.label?.props?.children
                          ? type.label.props.children
                          : type.label || "",
                      value: type.value,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="status_id"
                  initialValue={["pending"]}
                  rules={[{ required: true, message: "" }]}
                  style={{ marginBottom: "8px", width: "300px" }}
                >
                  {/* <DropdownCheckBox
                    name="status_id"
                    label="Select Status"
                    items={status}
                    options={options?.map((option) => ({
                      label: option.label,
                      value: option.value,
                    }))}
                    form={form}
                    selectedValues={status}
                    onChange={(selectedValues) => {
                      setStatus(selectedValues);
                      form.setFieldsValue({ status_id: selectedValues });
                    }}
                    dropdownMatchSelectWidth={false}
                  /> */}
                  <Select
                    placeholder="Select Status"
                    style={{ flex: 1, width: "100%" }}
                    onChange={(value) => setStatus([value])}
                    value={status[0] || undefined}
                    options={options?.map((option) => ({
                      label: option.label,
                      value: option.value,
                    }))}
                  />
                </Form.Item>
              </Col>
              {status?.[0]  !== "pending" &&
              <Col xs={24} sm={12} md={6}>
                <StyledRangePicker
                  style={{ width: "300px" }}
                  value={dateRange}
                  onChange={(e) => setDateRange(e)}
                />
              </Col>
              }
            </Row>
          </Form>
        </div>
        <Button
          className="mt-2"
          style={{ marginLeft: 0 }}
          disabled={!selectedRowKeys?.length > 0}
          onClick={handleBulkApprovalModal}
          type="primary"
          // loading={loading}
        >
          {" "}
          Approve{" "}
        </Button>
        <div className="timesheet">
          {/* <Spin spinning={loader || loading}> */}
          <Table
            dataSource={timeSheets}
            columns={timeSheetColumns}
            onRow={(record) => ({
              onClick: (e) => {
                e.stopPropagation();
                history.push(
                  `/timesheet-detailed/${record.week}/${record.employeeId}/${record.timesheet_id}/${status}`
                );
              },
            })}
            rowKey="key"
            pagination={true}
          />
          {/* </Spin> */}
        </div>
      </TimeSheetWrap>
    </>
  );
};

export default TimeSheetApproval;

const StyledRangePicker = styled(RangePicker)``;
const TimeSheetWrap = styled.div`
  .filters {
    display: flex;
    gap: 12px;
    justify-content: start;
    align-items: center;
    flex-wrap: wrap;
  }
  .timesheet {
    padding-top: 10px;
  }
  .action-buttons {
    display: flex;
    justify-content: flex-start;
    gap: 10px;
    font-weight: 600;
  }
  .period {
    font-weight: 600;
    color: #397ef3;
    cursor: pointer;
  }
  .employee-name {
    font-weight: 600;
  }
`;
